import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/api";

const RoadmapContext = createContext(null);

const API_BASE = BACKEND_URL + "/api";

function taskHash(text) {
  const s = text.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().substring(0, 120);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return "task-" + (h >>> 0).toString(36);
}

export function RoadmapProvider({ children }) {
  const [theme, setTheme] = useState(() =>
    localStorage.getItem("theme") || "light"
  );
  const [taskStatuses, setTaskStatuses] = useState({});
  const [taskTabs, setTaskTabs] = useState({});
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyOffset = useRef(0);
  const historyLoading = useRef(false);

  const headers = useMemo(() => {
    return {
      "Content-Type": "application/json",
    };
  }, []);

  // Theme
  useEffect(() => {
    if (theme === "dark") document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const loadTheme = useCallback(async () => {
    try {
      const r = await fetch(API_BASE + "/user/theme", { headers });
      if (r.ok) { const d = await r.json(); setTheme(d.theme); }
    } catch {}
  }, [headers]);

  const toggleTheme = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    fetch(API_BASE + "/user/theme", { method: "PUT", headers, body: JSON.stringify({ theme: next }) }).catch(() => {});
  }, [theme, headers]);

  // Task statuses
  const loadTaskStatuses = useCallback(async () => {
    try {
      const r = await fetch(API_BASE + "/tasks/statuses", { headers });
      if (r.ok) {
        const list = await r.json();
        const map = {};
        const tabs = {};
        list.forEach((s) => {
          map[s.task_id] = s.status;
          if (s.tab) tabs[s.task_id] = s.tab;
        });
        setTaskStatuses(map);
        setTaskTabs(tabs);
      }
    } catch {}
  }, [headers]);

  const saveTaskStatus = useCallback(async (taskId, label, tab, status) => {
    setTaskStatuses((prev) => ({ ...prev, [taskId]: status }));
    setTaskTabs((prev) => ({ ...prev, [taskId]: tab }));
    try {
      await fetch(API_BASE + "/tasks/status", {
        method: "PUT", headers,
        body: JSON.stringify({ task_id: taskId, task_label: label, tab, status }),
      });
    } catch {}
  }, [headers]);

  const getTaskStatus = useCallback((taskId) => taskStatuses[taskId] || "idle", [taskStatuses]);

  // Per-tab stats derived from statuses + tabs
  const tabStats = useMemo(() => {
    const stats = {};
    Object.entries(taskStatuses).forEach(([id, status]) => {
      const tab = taskTabs[id];
      if (!tab) return;
      if (!stats[tab]) stats[tab] = { done: 0, inProgress: 0 };
      if (status === "done") stats[tab].done++;
      else if (status === "in-progress") stats[tab].inProgress++;
    });
    return stats;
  }, [taskStatuses, taskTabs]);

  // History
  const loadHistory = useCallback(async (reset) => {
    if (historyLoading.current) return;
    if (reset) { historyOffset.current = 0; setHistoryItems([]); setHistoryTotal(0); }
    historyLoading.current = true;
    try {
      const r = await fetch(API_BASE + "/tasks/history?skip=" + historyOffset.current + "&limit=30", { headers });
      if (r.ok) {
        const d = await r.json();
        setHistoryTotal(d.total);
        setHistoryItems((prev) => reset ? d.items : [...prev, ...d.items]);
        historyOffset.current += d.items.length;
      }
    } catch {}
    historyLoading.current = false;
  }, [headers]);

  const canLoadMoreHistory = historyOffset.current < historyTotal;

  // Init
  useEffect(() => {
    loadTheme();
    loadTaskStatuses();
  }, [loadTheme, loadTaskStatuses]);

  const value = useMemo(() => ({
    theme, toggleTheme,
    taskStatuses, getTaskStatus, saveTaskStatus, taskHash,
    tabStats,
    historyItems, historyTotal, loadHistory, canLoadMoreHistory,
  }), [theme, toggleTheme, taskStatuses, getTaskStatus, saveTaskStatus, tabStats, historyItems, historyTotal, loadHistory, canLoadMoreHistory]);

  return <RoadmapContext.Provider value={value}>{children}</RoadmapContext.Provider>;
}

export function useRoadmap() {
  const ctx = useContext(RoadmapContext);
  if (!ctx) throw new Error("useRoadmap must be inside RoadmapProvider");
  return ctx;
}

export { taskHash };
